import React from 'react';
import { User } from 'lucide-react';
import { FilterButton, FilterDropdown } from '../../../ui';
import { UserFilter } from '../../../dashboard/types/dashboardTypes';
import { formatUserDisplayName } from '../../../../lib/utils';
import { useViewport } from '../../../../contexts/ViewportContext';

/**
 * Task assignee filter dropdown component
 * Used to filter tasks by assigned user/collaborator
 * Displayed in the task topic filter toolbar
 */
interface TaskAssigneeFilterDropdownProps {
  userFilters: UserFilter[];
  isOpen: boolean;
  onToggle: () => void;
  onSelect: (userId: string, isExclusive: boolean) => void;
  isMobile: boolean;
}

const TaskAssigneeFilterDropdown: React.FC<TaskAssigneeFilterDropdownProps> = ({
  userFilters,
  isOpen,
  onToggle,
  onSelect,
  isMobile,
}) => {
  const { isMobile: isViewportMobile } = useViewport();
  const actualMobile = isMobile || isViewportMobile;

  // Don't render anything if there are no users to filter
  if (userFilters.length === 0) {
    return null;
  }

  // Convert to format expected by FilterDropdown
  const filterOptions = userFilters.map(filter => ({
    id: filter.id,
    label: formatUserDisplayName(filter.name),
    avatar: filter.avatar_url ? (
      <img
        src={filter.avatar_url}
        alt={filter.name}
        className="w-6 h-6 rounded-full object-cover flex-shrink-0"
      />
    ) : (
      <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium flex-shrink-0">
        {formatUserDisplayName(filter.name)?.charAt(0) || '?'}
      </div>
    ),
    icon: <User size={16} className="text-blue-500" />,
  }));

  // Calculate selected values
  const selectedValues = userFilters.filter(filter => filter.active).map(filter => filter.id);

  // Handle selection changes
  const handleChange = (newSelectedValues: string[]) => {
    // Calculate which IDs were added or removed
    const previousSelectedValues = selectedValues;

    // If this was a multi-select operation (Ctrl/Cmd key)
    if (
      newSelectedValues.length > 1 ||
      (previousSelectedValues.length > 0 && newSelectedValues.length > 0)
    ) {
      // For each value that changed, toggle it
      const allValues = [...new Set([...previousSelectedValues, ...newSelectedValues])];

      for (const id of allValues) {
        // If it's in the new selection but wasn't in the previous, add it
        if (newSelectedValues.includes(id) && !previousSelectedValues.includes(id)) {
          onSelect(id, false); // Not exclusive
        }
        // If it was in the previous but not in the new, remove it
        else if (previousSelectedValues.includes(id) && !newSelectedValues.includes(id)) {
          onSelect(id, false); // Not exclusive
        }
      }
    } else {
      // Single selection - pass true for exclusive
      const selectedId = newSelectedValues[0];
      if (selectedId) {
        onSelect(selectedId, true);
      } else if (previousSelectedValues.length === 1) {
        // Deselecting the only selected item
        onSelect(previousSelectedValues[0], true);
      }
    }
  };

  // Handle clearing all filters
  const handleClear = () => {
    // Clear all active user filters
    userFilters.forEach(filter => {
      if (filter.active) {
        onSelect(filter.id, false);
      }
    });
  };

  // Get button text based on selected users
  const getButtonText = () => {
    const activeUsers = userFilters.filter(filter => filter.active);

    if (activeUsers.length === 0) {
      return 'People';
    } else if (activeUsers.length === 1) {
      return formatUserDisplayName(activeUsers[0].name);
    } else {
      return `Users (${activeUsers.length})`;
    }
  };

  // Custom button to show in the dropdown using the reusable FilterButton
  const userFilterButton = (
    <FilterButton
      isActive={isOpen}
      hasFilters={selectedValues.length > 0}
      onClick={onToggle}
      onClear={e => {
        e.stopPropagation();
        handleClear();
        onToggle();
      }}
      label={getButtonText()}
      icon={<User size={16} className="text-blue-500" />}
      isMobile={actualMobile}
      className=""
    />
  );

  // Don't render the button in mobile view - it will be in a menu
  if (actualMobile && !isOpen) {
    return null;
  }

  return (
    <FilterDropdown
      options={filterOptions}
      selectedValues={selectedValues}
      onChange={handleChange}
      onClear={handleClear}
      allowMultiple={true}
      label="People"
      placeholder="People"
      headerText="People"
      isMobile={actualMobile}
      open={isOpen}
      onOpenChange={onToggle}
      mobilePosition={0.2}
      trigger={userFilterButton}
      className="z-[9999]"
    />
  );
};

export default TaskAssigneeFilterDropdown;
